import React, { useEffect, useState } from "react";
import axios from "axios";

const api = axios.create({ baseURL: "", withCredentials: true });

/**
 * Account Core picker + repository binding. Field names match the API:
 * coreId, coreVersion, slug, githubOwner, githubRepo, commitSha.
 */
export default function CoreOnboardingCard() {
  const [cores, setCores] = useState([]);
  const [envelope, setEnvelope] = useState(null);
  const [slug, setSlug] = useState("");
  const [coreId, setCoreId] = useState("blank");
  const [coreVersion, setCoreVersion] = useState("");
  const [owner, setOwner] = useState("");
  const [repo, setRepo] = useState("");
  const [commitSha, setCommitSha] = useState("");
  const [binding, setBinding] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [CoreOnboardingCard] render`);
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/deploy/cores");
        if (cancelled) return;
        setCores(res.data.cores || []);
        setEnvelope(res.data.envelope || null);
        const first = (res.data.cores || [])[0];
        if (first) {
          setCoreId(first.coreId);
          setCoreVersion(first.version);
        }
      } catch (err) {
        if (!cancelled) setMsg(err.response?.data?.error || "Could not load Cores.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const select = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const res = await api.post("/api/deploy/cores/select", { slug, coreId, coreVersion });
      setMsg(`Core selected: ${res.data.project.coreId}@${res.data.project.coreVersion}`);
    } catch (err) {
      setMsg(err.response?.data?.error || "Select failed.");
    } finally { setBusy(false); }
  };

  const bind = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const res = await api.post(`/api/deploy/projects/${encodeURIComponent(slug)}/repository-binding`, {
        githubOwner: owner,
        githubRepo: repo,
      });
      setBinding(res.data.binding);
      setMsg(res.data.fallback?.message || "Repository recorded. Push the Core tree, then verify the commit.");
    } catch (err) {
      setMsg(err.response?.data?.error || "Bind failed.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const res = await api.post(`/api/deploy/projects/${encodeURIComponent(slug)}/repository-binding/verify`, {
        commitSha,
      });
      setBinding(res.data.binding);
      setMsg("Repository bound. You can deploy this commit.");
    } catch (err) {
      setMsg(err.response?.data?.error || "Verify failed.");
    } finally { setBusy(false); }
  };

  const input = "w-full bg-black border border-gray-700 rounded px-3 py-2 text-sm text-silver-light";

  return (
    <div className="bg-off-black border border-gray-800 rounded-xl p-6 space-y-4">
      <h2 className="text-silver-light font-medium">Start from a Core</h2>
      <p className="text-xs text-silver-dark">
        V1 named cohort: one project, customer-owned GitHub repo, no PAT stored.
        {envelope ? ` Platform €${envelope.platformPriceEur}/month. €${envelope.includedInfraAllowanceEur} included infrastructure ${envelope.includedInfraTreatment}.` : ""}
      </p>
      {cores.length === 0 ? (
        <p className="text-sm text-silver-dark">No active Cores are pickable yet. Blank must be activated with a proof digest first.</p>
      ) : (
        <ul className="text-sm text-silver-medium space-y-1">
          {cores.map((c) => (
            <li key={`${c.coreId}@${c.version}`}>
              <strong className="text-silver-light">{c.coreId}@{c.version}</strong> · {c.runtime} · health {c.healthPath} · {c.costProfile}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={select} className="grid sm:grid-cols-3 gap-3">
        <input className={input} required placeholder="project-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
        <select className={input} value={`${coreId}@${coreVersion}`} onChange={(e) => { const [id, ver] = e.target.value.split("@"); setCoreId(id); setCoreVersion(ver); }}>
          {cores.map((c) => <option key={`${c.coreId}@${c.version}`} value={`${c.coreId}@${c.version}`}>{c.coreId}@{c.version}</option>)}
        </select>
        <button disabled={busy || !cores.length} className="bg-lime-600 hover:bg-lime-500 text-black text-sm font-semibold rounded px-3 py-2 disabled:opacity-40">Select Core</button>
      </form>
      <form onSubmit={bind} className="grid sm:grid-cols-3 gap-3">
        <input className={input} required placeholder="github owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        <input className={input} required placeholder="empty repo name" value={repo} onChange={(e) => setRepo(e.target.value)} />
        <button disabled={busy || !slug} className="border border-gray-600 text-silver-light text-sm rounded px-3 py-2">Bind repo</button>
      </form>
      <form onSubmit={verify} className="grid sm:grid-cols-2 gap-3">
        <input className={input} required placeholder="40-char commit sha after you push" value={commitSha} onChange={(e) => setCommitSha(e.target.value)} />
        <button disabled={busy || !slug} className="border border-gray-600 text-silver-light text-sm rounded px-3 py-2">Verify tree</button>
      </form>
      {binding && <p className="text-xs text-silver-dark font-mono">status={binding.status} sha={binding.boundCommitSha || "—"}</p>}
      {msg && <p className="text-xs text-lime-400">{msg}</p>}
    </div>
  );
}
